import { useState } from 'react';
import { usePlan } from '../store/planStore.js';
import { loadPlan } from '../store/db.js';
import { addDays, formatUk, isoDate, weekDates, weekdayOf } from '../../domain/calendar.js';
import { LUNCH_COMPONENTS } from '../../data/index.js';
import type { LunchComponentType } from '../../domain/types.js';

const ORDER: LunchComponentType[] = ['base', 'filling', 'side', 'snack', 'drink'];

export function Lunches({ onDone }: { onDone: () => void }): React.JSX.Element {
  const { ready, plan, settings, setLunch } = usePlan();
  const [note, setNote] = useState<string | null>(null);
  if (!ready) return <p>Loading</p>;

  const dates = weekDates(isoDate(plan.weekStart)).map(String);

  /** Only the person-and-day pairs that genuinely need a lunch box. */
  const slots = dates.flatMap((date) =>
    settings.people
      .filter((person) => person.lunchDays.includes(weekdayOf(isoDate(date))))
      .map((person) => ({ date, person })),
  );

  const packed = slots.filter(({ date, person }) =>
    plan.lunches.some((l) => l.date === date && l.personId === person.id && l.componentIds.length > 0),
  ).length;

  const chosenFor = (date: string, personId: string): string[] =>
    plan.lunches.find((l) => l.date === date && l.personId === personId)?.componentIds ?? [];

  /**
   * The previous day THIS person had a lunch, not merely the previous day
   * anyone did. The twins share nursery days today, but Settings lets them
   * diverge — and then "same as yesterday" would resolve to a date only the
   * other child has, silently copying an empty lunch.
   */
  const previousLunchDate = (date: string, personId: string): string | null =>
    slots
      .filter((s) => s.person.id === personId && s.date < date)
      .map((s) => s.date)
      .at(-1) ?? null;

  return (
    <section className="lunches">
      <header className="plan-head">
        <h1>Nursery lunches</h1>
        <p className="plan-count">{`${packed} of ${slots.length} packed`}</p>
        <button
          type="button"
          className="ghost"
          onClick={() => {
            void (async () => {
              const lastWeek = await loadPlan(addDays(isoDate(plan.weekStart), -7));
              if (!lastWeek || lastWeek.lunches.length === 0) {
                setNote('Last week had no lunches to copy.');
                return;
              }
              let copied = 0;
              let stale = 0;
              for (const previous of lastWeek.lunches) {
                const date = addDays(isoDate(previous.date), 7);
                const person = settings.people.find((p) => p.id === previous.personId);

                // The engine THROWS on a lunch for someone who has no nursery
                // that day, and a nursery pattern can change between weeks.
                // Copying one forward blind would poison the plan invisibly —
                // no slot renders for it, so it cannot be removed from here —
                // and the shopping list would crash days later with nothing
                // pointing back at this button.
                if (!person || !person.lunchDays.includes(weekdayOf(date))) {
                  stale += 1;
                  continue;
                }

                // Never overwrite a lunch already packed this week. This is a
                // shortcut for filling empty slots, not a reset.
                const alreadyPacked = plan.lunches.some(
                  (l) => l.date === date && l.personId === previous.personId && l.componentIds.length > 0,
                );
                if (alreadyPacked) continue;

                setLunch(date, previous.personId, previous.componentIds);
                copied += 1;
              }

              setNote(
                stale > 0
                  ? `Copied ${copied}. Skipped ${stale} on days nobody has nursery now.`
                  : null,
              );
            })();
          }}
        >
          Copy last week
        </button>
        {note ? <p className="empty-note">{note}</p> : null}
      </header>

      {slots.map(({ date, person }) => {
        const chosen = chosenFor(date, person.id);
        const yesterday = previousLunchDate(date, person.id);
        // B8: an empty `[]` is what `setLunch` uses to DELETE a lunch (see the
        // reducer). A previous lunch day that has nothing packed into it yet
        // is exactly that empty array, so offering "Same as yesterday" here
        // would silently wipe whatever this day already holds the moment it
        // is tapped.
        const yesterdayLunch = yesterday ? chosenFor(yesterday, person.id) : [];
        return (
          <article className="lunch-slot" data-testid="lunch-slot" key={`${date}:${person.id}`}>
            <h2>
              {person.name} &mdash; {formatUk(isoDate(date)).replace(/ \d{4}$/, '')}
            </h2>

            {yesterdayLunch.length > 0 ? (
              <button
                type="button"
                className="ghost"
                onClick={() => setLunch(date, person.id, yesterdayLunch)}
              >
                Same as yesterday
              </button>
            ) : null}

            {ORDER.map((type) => (
              <div className="lunch-row" key={type}>
                <span className="lunch-type">{type}</span>
                <div className="lunch-options">
                  {LUNCH_COMPONENTS.filter((c) => c.type === type).map((component) => {
                    const on = chosen.includes(component.id);
                    return (
                      <button
                        key={component.id}
                        type="button"
                        aria-pressed={on}
                        onClick={() =>
                          setLunch(
                            date,
                            person.id,
                            on ? chosen.filter((id) => id !== component.id) : [...chosen, component.id],
                          )
                        }
                      >
                        {component.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </article>
        );
      })}

      <button type="button" className="primary" onClick={onDone}>Done</button>
    </section>
  );
}
